import React, { useState, useEffect } from 'react';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { BottomTabBar } from './components/BottomTabBar';
import { GlobalSearchModal } from './components/GlobalSearchModal';
import { SignInModal } from './components/SignInModal';
import { I18nProvider } from './services/i18n';

// Views
import { HomeView } from './views/HomeView';
import { AppsView } from './views/AppsView';
import { AppProfileView } from './views/AppProfileView';
import { TrendingView } from './views/TrendingView';
import { ChartsView } from './views/ChartsView';
import { DevelopersView } from './views/DevelopersView';
import { CompareView } from './views/CompareView';
import { KeywordsView } from './views/KeywordsView';
import { AIInsightsView } from './views/AIInsightsView';
import { PricingView } from './views/PricingView';
import { WatchlistView } from './views/WatchlistView';
import { CollectionsView } from './views/CollectionsView';
import { CategoryPageView } from './views/CategoryPageView';
import { MarketPageView } from './views/MarketPageView';
import { NewAppsView } from './views/NewAppsView';
import { LaunchRadarView } from './views/LaunchRadarView';
import { DebugIconsView } from './views/DebugIconsView';
import { DebugCatalogView } from './views/DebugCatalogView';
import { DataQualityView } from './views/DataQualityView';

export function AppContent() {
  const [currentRoute, setCurrentRoute] = useState<string>(() => {
    return window.location.pathname !== '/' ? window.location.pathname : window.location.hash.replace('#', '') || '/';
  });

  const [searchOpen, setSearchOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [compareApps, setCompareApps] = useState<string[]>(['duolingo', 'babbel', 'busuu']);

  // Sync state with browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname !== '/' ? window.location.pathname : window.location.hash.replace('#', '') || '/';
      setCurrentRoute(path);
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handlePopState);

    // Keyboard shortcut CMD+K or CTRL+K for search
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handlePopState);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const navigateTo = (route: string) => {
    setCurrentRoute(route);
    if (window.history.pushState) {
      window.history.pushState(null, '', route);
    } else {
      window.location.hash = route;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectApp = (appIdOrSlug: string) => {
    navigateTo(`/app/${appIdOrSlug}`);
  };

  const handleSelectDeveloper = (devSlug: string) => {
    navigateTo(`/developer/${devSlug}`);
  };

  const handleCompare = (appIds: string[]) => {
    setCompareApps(appIds);
    navigateTo('/compare');
  };

  // Route Resolver
  const renderCurrentView = () => {
    if (currentRoute === '/' || currentRoute === '') {
      return (
        <HomeView
          onNavigate={navigateTo}
          onSelectApp={handleSelectApp}
          onSelectDeveloper={handleSelectDeveloper}
        />
      );
    }

    if (currentRoute === '/apps') {
      return (
        <AppsView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
        />
      );
    }

    if (currentRoute.startsWith('/app/')) {
      const pathParts = currentRoute.replace('/app/', '').split('?')[0].split('/');
      const appIdentifier = pathParts[0];
      return (
        <AppProfileView
          appId={appIdentifier || 'duolingo'}
          onNavigate={navigateTo}
          onSelectApp={handleSelectApp}
          onSelectDeveloper={handleSelectDeveloper}
          onCompare={handleCompare}
          onOpenSignIn={() => setSignInOpen(true)}
        />
      );
    }

    if (currentRoute === '/new-apps') {
      return (
        <NewAppsView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
        />
      );
    }

    if (currentRoute === '/launch-radar') {
      return (
        <LaunchRadarView
          onSelectApp={handleSelectApp}
        />
      );
    }

    if (currentRoute === '/trending' || currentRoute === '/discovery') {
      return (
        <TrendingView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
        />
      );
    }

    if (currentRoute === '/charts') {
      return (
        <ChartsView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
        />
      );
    }

    if (currentRoute === '/categories') {
      return (
        <CategoryPageView
          onSelectApp={handleSelectApp}
          onNavigate={navigateTo}
        />
      );
    }

    if (currentRoute.startsWith('/category/')) {
      const catSlug = currentRoute.replace('/category/', '').split('?')[0].split('/')[0];
      return (
        <CategoryPageView
          categorySlug={catSlug}
          onSelectApp={handleSelectApp}
          onNavigate={navigateTo}
        />
      );
    }

    if (currentRoute === '/markets') {
      return (
        <MarketPageView
          onSelectApp={handleSelectApp}
          onNavigate={navigateTo}
        />
      );
    }

    if (currentRoute.startsWith('/market/')) {
      const marketCode = currentRoute.replace('/market/', '').split('?')[0].split('/')[0];
      return (
        <MarketPageView
          marketCode={marketCode}
          onSelectApp={handleSelectApp}
          onNavigate={navigateTo}
        />
      );
    }

    if (currentRoute === '/developers') {
      return (
        <DevelopersView
          initialDevSlug={null}
          onSelectApp={handleSelectApp}
          onSelectDeveloper={handleSelectDeveloper}
        />
      );
    }

    if (currentRoute.startsWith('/developer/')) {
      const devSlug = currentRoute.replace('/developer/', '').split('?')[0].split('/')[0];
      return (
        <DevelopersView
          initialDevSlug={devSlug}
          onSelectApp={handleSelectApp}
          onSelectDeveloper={handleSelectDeveloper}
        />
      );
    }

    if (currentRoute === '/compare') {
      return (
        <CompareView
          initialAppIds={compareApps}
          onSelectApp={handleSelectApp}
        />
      );
    }

    if (currentRoute === '/keywords') {
      return (
        <KeywordsView
          onSelectApp={handleSelectApp}
        />
      );
    }

    if (currentRoute === '/ai-insights') {
      return (
        <AIInsightsView
          onSelectApp={handleSelectApp}
        />
      );
    }

    if (currentRoute === '/pricing') {
      return <PricingView />;
    }

    if (currentRoute === '/watchlist') {
      return (
        <WatchlistView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
          onNavigate={navigateTo}
        />
      );
    }

    if (currentRoute === '/collections') {
      return (
        <CollectionsView
          onSelectApp={handleSelectApp}
          onCompare={handleCompare}
        />
      );
    }

    if (currentRoute === '/debug-icons') {
      return <DebugIconsView />;
    }

    if (currentRoute === '/debug/catalog' || currentRoute === '/debug-catalog') {
      return <DebugCatalogView />;
    }

    if (
      currentRoute === '/admin/data-quality' ||
      currentRoute === '/data-quality' ||
      currentRoute === '/admin/quality' ||
      currentRoute === '/provenance'
    ) {
      return <DataQualityView onNavigateToApp={handleSelectApp} />;
    }

    // Default Fallback to Home
    return (
      <HomeView
        onNavigate={navigateTo}
        onSelectApp={handleSelectApp}
        onSelectDeveloper={handleSelectDeveloper}
      />
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] text-slate-900 font-sans antialiased selection:bg-blue-600 selection:text-white flex flex-col justify-between pb-28 md:pb-0">
      <div>
        {/* Top Navbar */}
        <Navbar
          currentRoute={currentRoute}
          onNavigate={navigateTo}
          onOpenSearch={() => setSearchOpen(true)}
          onOpenSignIn={() => setSignInOpen(true)}
        />

        {/* Dynamic View Content */}
        {renderCurrentView()}
      </div>

      {/* Footer */}
      <Footer onNavigate={navigateTo} />

      {/* Bottom iOS Navigation Bar */}
      <BottomTabBar
        currentRoute={currentRoute}
        onNavigate={navigateTo}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenSignIn={() => setSignInOpen(true)}
      />

      {/* Modals */}
      <GlobalSearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelectApp={handleSelectApp}
        onSelectDeveloper={handleSelectDeveloper}
      />

      <SignInModal
        isOpen={signInOpen}
        onClose={() => setSignInOpen(false)}
      />
    </div>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AppContent />
    </I18nProvider>
  );
}

export default App;
